'use strict';

import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from '@angular/core';
import {CurationActionCreatorService} from '../../../services/curation-action-creator.service';
import {CurationEditorAbstract} from '../curation-editor-abstract';
import {DynamicModalLoaderService} from '@ispm/layout/services/dynamic-modal-loader.service';
import {WorkflowBarActionsCreatorService} from '@ispm/shared/components/workflow-bar/services/workflow-bar-actions-creator.service';
import {PdfService} from '@ispm/test/services/pdf.service';
import {IdynamicFormItem} from '@ispm/shared/dynamic-form/interfaces/IdynamicFormItem';
import {CurationSettings} from '../../curation-dashboard/curation-setting';
import {PanelsService} from "@ispm/layout/services/panels.service";
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";
import {get, has, isEqual, set} from 'lodash';
import {TranslateService} from "@ngx-translate/core";

import {Discipline, DRUG_S, DRUGS_PROP, THERAPIES_PROP, THERAPY_S} from "@ispm/shared/constants/curation_constants";
import {FormHandlerService} from "@ispm/dynamic-form/services/form-handler.service";
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {DiagnosticOrderService} from '@ispm/dashboard/test/services/diagnostic-order.service';
import {ActivityService} from '@ispm/user/services/activity.service';
import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {WorkflowConfigService} from "@ispm/workflow/services/workflow-config.service";
import {NotesService} from '@ispm/user/services/notes.service';
import {NotificationMessageService} from '@ispm/core/components/customise-notification/notifications-message.service';
import {HttpInterceptorService} from "@ispm/core/services/http-interceptor.service";
import { ActivatedRoute, Router } from '@angular/router';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";


@Component({
  selector: 'gen-curation-editor-regimen',
  templateUrl: './curation-editor-regimen.component.html',
  styleUrls: ['./curation-editor-regimen.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})

export class CurationEditorRegimenComponent extends CurationEditorAbstract {
  public formData: any = {};
  public dataChanged: boolean = false;
  public hideSummaryAndDrugs: boolean = false;
  public isRadiationDiscipline: boolean = false;
  public isCardiology:boolean = false;
  public listTitle: string = 'Drugs List';

  constructor(public authService: AuthenticationService,
              formHandler: FormHandlerService,
              public notificationMessageService: NotificationMessageService,
              workflowBarActionsCreatorService: WorkflowBarActionsCreatorService,
              modalLoader: DynamicModalLoaderService,
              activatedRoute: ActivatedRoute,
              pdfService: PdfService,
              panelService: PanelsService,
              curationActionCreatorService: CurationActionCreatorService,
              translateService: TranslateService,
              protected diagnosticOrderService : DiagnosticOrderService,
              notesService: NotesService,
              coreAC: CoreActionCreatorService,
              detector: ChangeDetectorRef,
              public userActionCreatorService: UserActionCreatorService,
              public activityService: ActivityService,
              workflowConfigService: WorkflowConfigService,
              _http: HttpInterceptorService,
              router: Router,
              routerManagerService: RouterManagerService) {
    super(authService, formHandler, notificationMessageService, workflowBarActionsCreatorService,
      modalLoader, activatedRoute, panelService, notesService, coreAC, curationActionCreatorService, translateService,
      diagnosticOrderService, userActionCreatorService, activityService, _http, router, pdfService, detector, workflowConfigService, routerManagerService);
  }

  setInitParams() {
    // why we check if it is Radiation? Because only Radiation has therapies instead of drugs list.
    // Drugs list is relevant for any discipline which has only drugs
    if (this.relevantProperty) {
      this.isRadiationDiscipline = isEqual(get(this.relevantProperty, 'property', undefined), 'discipline.display')
        && isEqual(get(this.relevantProperty, 'value', undefined), Discipline.RADIATION_ONCOLOGY);
    }
  }

  prepareDataToSave(status: string): Promise<boolean> {
    const itemsArrayName = this.isRadiationDiscipline ? THERAPIES_PROP : DRUGS_PROP;
    const itemName = this.isRadiationDiscipline ? THERAPY_S : DRUG_S;

    // remove empty drugs or therapies
    if (has(this.content, itemsArrayName)) {
      set(this.content, itemsArrayName, this.cleanData(get(this.content, itemsArrayName, undefined), itemName));
    }
    // Remove deleted drugs/therapies before approve
    if (this.curationStatus == CurationSettings.CURATION_STATUS_WAITING_FOR_APPROVAL || status == CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED) {
      return this.removeDeletedItems([itemsArrayName], this.curationStatus, status != CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED).then(() => {
        return Promise.resolve(true);
      }).catch((err) => {
        console.error(err);
        return Promise.reject(err);
      });
    }
    else {
      return Promise.resolve(true);
    }
  }

  public getCurationKey(): string {
    return this.content['externalAutoGen'] + ' ' + this.content['regimenName'];
  }

  updateForm(form: any, updateFieldKeys: any) {
    if (form) {
      // This is a bypass that should be removed when bug #32557 is fixed. The problem with this bypass is that it does not allow the user to delete selected regimen
      if (updateFieldKeys == null || (updateFieldKeys[0] === 'DFCIRegimen' && form[updateFieldKeys] == null)) {
        return;
      }
      if (updateFieldKeys !== null && updateFieldKeys instanceof Object) {
        for (const prop in form) { // This part solved this issue GEN-33470
          if (!form.hasOwnProperty(prop)) {
            return;
          }
        }
      }
      // set data by form
      for (const prop in form) {
        this.content[prop] = form[prop];
      }
      this.curationKey = this.getCurationKey();
    }
  }

  validationEvent(valid) {
    this.isValid = valid;
  }

  deleteItem($event) {
    // remove deleted element from validation
    this.validationFormsMap.delete($event.key);
    this.calculateValidationState();
  }

  postGetCurationItem() {
    this.isDataLoaded = true;
    this.detector.detectChanges();
  }

  postUpdateCurationItem() {
    this.updateForm(this.content, null);
  }

  public itemOverridesDrug(value): any {
    const formOverrides: { [itemName: string]: Partial<IdynamicFormItem> } = {
      'drug': {
        'attributes': {'previewOnly': false}
      }
    };
    return formOverrides;
  }

  public itemOverridesTherapy(value): any {
    const formOverrides: { [itemName: string]: Partial<IdynamicFormItem> } = {
      'therapy': {
        'attributes': {'previewOnly': false}
      }
    };
    return formOverrides;
  }

  public getItemOverrides(){
    return this.isRadiationDiscipline ? this.itemOverridesTherapy: this.itemOverridesDrug;
  }

  itemUpdate(items: any) {
    if(this.isRadiationDiscipline) {
      set(this.curationData, this.listDataPath, this.removeDuplicatedTherapies(items));
    }
    else {
      set(this.curationData, this.listDataPath, this.removeDuplicatedDrugs(items));
    }
  }

  private calculateValidationState() {
    let valid: boolean = true;
    this.validationFormsMap.forEach((value: boolean, key: string) => {
      if (value === false) {
        valid = false;
      }
    });
    this.isValid = valid;
    this.calculateState();
  }

  private removeDuplicatedDrugs(drugsList: any): any {
    const allUniqueDrugs = [];
    let newDrugsList = drugsList;

    // removing duplicate drugs
    drugsList.forEach((drugs, idxDL) => {
      if (!drugs.deleted && drugs.drug && drugs.drug.code && allUniqueDrugs.indexOf(drugs.drug.code) === -1) {
        allUniqueDrugs[idxDL] = drugs.drug.code;
      } else if (!drugs.deleted && drugs.drug && allUniqueDrugs.length) {
        newDrugsList = newDrugsList.filter((item, idxNDL) => {
          return idxDL !== idxNDL;
        });
      }
    });

    return newDrugsList;
  }

  private removeDuplicatedTherapies(therapiesList: any): any {
    const allUniqueTherapies = [];
    let newTherapiesList = therapiesList;

    // removing duplicate drugs
    therapiesList.forEach((items, idxDL) => {
      if (!items.deleted && items.therapy && items.therapy.code && allUniqueTherapies.indexOf(items.therapy.code) === -1) {
        allUniqueTherapies[idxDL] = items.therapy.code;
      } else if (!items.deleted && items.therapy && allUniqueTherapies.length) {
        newTherapiesList = newTherapiesList.filter((item, idxNDL) => {
          return idxDL !== idxNDL;
        });
      }
    });

    return newTherapiesList;
  }

   public updateSummaryForm(form: any, updateFieldKeys: any) {
     updateFieldKeys.forEach(key => this.curationData['content'][key] = form[key]);
   }
}
